import React, { useEffect } from 'react'
import Routers from './routes'
import { BrowserRouter } from 'react-router-dom'
import { createGlobalStyle } from 'styled-components'
import { initClarity, identifyUser } from './utils/clarity'
import { identifyPostHogUser, initPostHog } from './utils/posthog'
import { shouldEnforceFullscreen } from './utils/deviceTier'
// const { REACT_APP_CLARITY_PROJECT_ID } = process.env
const { REACT_APP_POSTHOG_API_KEY } = process.env;
const { REACT_APP_POSTHOG_HOST } = process.env;
const { ENVIRONMENT } = process.env;

const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Plus Jakarta Sans', sans-serif;
  }

  /* iPhone-only: let candidates SCROLL to the bottom action buttons that
     otherwise sit behind Safari's un-hideable bottom toolbar. The assessment
     containers are height:100vh / position:fixed, which traps the footer; on
     iPhone we un-lock vertical scroll and add bottom clearance so every
     section's Next / Submit / Record / Run / Mic button is reachable.

     Gated on body.assessment-mobile (added only on iPhone via deviceTier) + the
     presence of the .exam-shell hook (only rendered inside an assessment), so
     desktop and iPad are completely unaffected. NOTE: we intentionally do NOT
     require .assessment-fullscreen-mode here — on iPhone (LITE) fullscreen is
     skipped, so that class is absent. No global viewport change. */
  body.assessment-mobile .exam-shell,
  body.assessment-mobile .exam-scroll {
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow: visible !important;
    position: relative !important;
  }
  body.assessment-mobile .exam-shell {
    min-height: 100vh;
    /* clears Safari's bottom toolbar (~50-80px) + the home indicator so the
       scrolled-to action buttons are fully tappable. env() adds the real safe
       area when available, else falls back to 0. */
    padding-bottom: calc(104px + env(safe-area-inset-bottom, 0px));
  }
  /* footers are pinned (sticky/absolute) to the bottom of a 100vh container —
     reset to normal flow on iPhone so they sit at the natural page bottom and
     scroll into reach instead of staying behind the toolbar. */
  body.assessment-mobile .exam-actions {
    position: static !important;
  }
  /* belt: if any assessment ever sets fullscreen-mode (which locks body scroll)
     on a phone, keep the page scrollable so the un-locked container can scroll. */
  body.assessment-mobile.assessment-fullscreen-mode {
    overflow: visible !important;
    height: auto !important;
  }
`

const App = ({ token, studentData, getStudentData, SignIn, studentDetails }) => {
  useEffect(() => {
    // iPhone (LITE tier) → enable the mobile scroll-to-reach-buttons CSS above.
    // shouldEnforceFullscreen() is false only on iPhone/in-app; desktop & iPad
    // keep their existing behaviour.
    try {
      if (!shouldEnforceFullscreen()) {
        document.body.classList.add('assessment-mobile')
      }
    } catch (_) {}
  }, [])

  useEffect(() => {
    // PostHog has to init on mount, NOT inside the `email` effect below: the OTP
    // invite flow (/assessment/start/:token → /assessment/take/:id) is routed
    // around AuthPage by AuthRouter, so getStudentData never runs, studentDetails
    // stays empty and posthog was never initialised — every assessment_* event an
    // invited candidate produced was dropped. Identify still happens once we know
    // who the person is (below for the portal, on OTP verify for invites).
    initPostHog(REACT_APP_POSTHOG_API_KEY, REACT_APP_POSTHOG_HOST)

    // if (REACT_APP_CLARITY_PROJECT_ID != 'null') {
    //   initClarity(REACT_APP_CLARITY_PROJECT_ID)
    // }
  }, [])


  useEffect(() => {
    const email = studentDetails?.studentPersonalProfile?.primaryEmail || studentDetails?.admin?.email || studentData?.admin?.email
    if (email) {
      identifyUser(email)

      identifyPostHogUser(email, {
        // Environment
        environment: ENVIRONMENT,

        // Identity
        studentId: studentDetails?.id,
        email: email,
        prefixId: studentDetails?.prefixId,
        fullName: studentDetails?.fullName,
        
        // Institution Info
        instituteCampusId: studentDetails?.instituteCampusId,
        instituteCampusName: studentDetails?.instituteCampusName,
        
        // Course Info
        degree: studentDetails?.currentCourse?.degree,
        department: studentDetails?.currentCourse?.department,
        educationLevel: studentDetails?.currentCourse?.educationLevel,
        
        // Profile Status
        profileCompletionPerc: studentDetails?.profileCompletionPerc,
        status: studentDetails?.status,
        currentState: studentDetails?.currentState,
        
        // Account Type
        isCorporate: studentDetails?.isCorporate,
        accessLevel: studentDetails?.accessLevel,
        
        // Engagement metrics (optional)
        visitedDate: studentDetails?.visitedDate,
        lastLoginDate: studentDetails?.admin?.last_login_date,
        
        // Skills/Experience (optional)
        workExperience: studentDetails?.workExperience,
        skillCount: studentDetails?.skillIds?.length || 0
      })
      console.log("PostHog user identified with email:", email);
    }
  }, [studentData, studentDetails])

  return (
    <BrowserRouter>
      <GlobalStyle />
      <Routers token={token} getStudentData={getStudentData} SignIn={SignIn} />
    </BrowserRouter>
  )
}

export default App
